import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Crear instancia de axios
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para manejar errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// AUTH
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  registerDemo: (data) => api.post('/auth/register-demo', data),
  me: () => api.get('/auth/me'),
};

// ANIMALES
export const animalesAPI = {
  getAll: (params) => api.get('/animales/', { params }),
  getById: (id) => api.get(`/animales/${id}`),
  create: (data) => api.post('/animales/', data),
  update: (id, data) => api.put(`/animales/${id}`, data),
  delete: (id) => api.delete(`/animales/${id}`),
  deleteBatch: (animalIds) => api.post('/animales/eliminar-lote', { animal_ids: animalIds }),
  updateBatch: (data) => api.post('/animales/actualizar-lote', data),
  buscarPorRFID: (ide, establecimientoId) =>
    api.get(`/animales/buscar/rfid/${ide}`, { params: { establecimiento_id: establecimientoId } }),
  buscarGlobal: (establecimientoId, q, limit = 10) =>
    api.get('/animales/buscar-global', { params: { establecimiento_id: establecimientoId, q, limit } }),
  syncAll: (establecimientoId) => api.get(`/animales/sync/${establecimientoId}`),
  recalcularGMD: (animalId) => api.post(`/pesajes/recalcular-gmd/${animalId}`),
  kpisRodeo: (params) => api.get('/animales/kpis-rodeo', { params }),
};

// PESAJES
export const pesajesAPI = {
  getAll: (params) => api.get('/pesajes/', { params }),
  getByAnimal: (animalId) => api.get(`/pesajes/animal/${animalId}`),
  create: (data) => api.post('/pesajes/', data),
};

// DASHBOARD
export const dashboardAPI = {
  getKPIs: (establecimientoId) => api.get(`/dashboard/kpis/${establecimientoId}`),
  getKPIsCompletos: (establecimientoId) => api.get(`/dashboard/kpis-completos/${establecimientoId}`),
  getAlertas: (establecimientoId, limit = 50, prioridad = undefined) =>
    api.get(`/dashboard/alertas/${establecimientoId}`, { params: { limit, prioridad } }),
  generarAlertas: (establecimientoId) => api.post(`/dashboard/alertas/generar/${establecimientoId}`),
  resolverAlerta: (alertaId) => api.put(`/dashboard/alertas/${alertaId}/resolver`),
};

// PROYECCIONES
export const proyeccionesAPI = {
  getPreñez: (establecimientoId) => api.get(`/proyecciones/preñez/${establecimientoId}`),
  getParicion: (establecimientoId) => api.get(`/proyecciones/paricion/${establecimientoId}`),
  getProduccion: (establecimientoId) => api.get(`/proyecciones/produccion/${establecimientoId}`),
  getDescartes: (establecimientoId) => api.get(`/proyecciones/descartes/${establecimientoId}`),
  getRanking: (establecimientoId, topN = 100) =>
    api.get(`/proyecciones/ranking/${establecimientoId}`, { params: { top_n: topN } }),
  getCompleto: (establecimientoId) => api.get(`/proyecciones/completo/${establecimientoId}`),
};

// SERVICIOS
export const serviciosAPI = {
  create: (data) => api.post('/servicios/', data),
  getAll: (params) => api.get('/servicios/', { params }),
};

// PARICIONES
export const paricionesAPI = {
  create: (data) => api.post('/pariciones/', data),
  getAll: (params) => api.get('/pariciones/', { params }),
};

// CONTROLES MACHOS
export const controlesMachosAPI = {
  create: (data) => api.post('/controles_machos/', data),
};

// SCORING & GDR
export const scoringAPI = {
  evalAnimal: (animalId) => api.get(`/scoring/animal/${animalId}`),
  evalCohorte: (establecimientoId, sexo, edadMin, edadMax) =>
    api.get(`/scoring/cohorte/${establecimientoId}`, { params: { sexo, edad_min: edadMin, edad_max: edadMax } }),
  registrarGDR: (animalId, gdr) => api.post(`/scoring/gdr/${animalId}?gdr=${gdr}`),
};

// EXPORTACION
export const exportacionAPI = {
  animales: (establecimientoId, formato = 'xlsx', filtros = {}) => {
    const params = { formato, ...filtros };
    return api.get(`/exportacion/animales/${establecimientoId}`, { params, responseType: 'blob' });
  },
  rodeoCompleto: (establecimientoId) =>
    api.get(`/exportacion/rodeo-completo/${establecimientoId}`, { responseType: 'blob' }),
};

// REPORTES PDF
export const reportesAPI2 = {
  pdfIA: (establecimientoId) =>
    api.get(`/reportes/pdf-ia/${establecimientoId}`, { responseType: 'blob' }),
  datosReporte: (establecimientoId) =>
    api.get(`/reportes/data/${establecimientoId}`),
};

// ALERTAS EMAIL
export const emailAlertasAPI = {
  enviarCriticas: (establecimientoId, email) =>
    api.post(`/email-reports/alertas-criticas/${establecimientoId}`, { email }),
};

// EVALUACION MACHOS
export const evaluacionMachosAPI = {
  getControles: (animalId) => api.get(`/controles_machos/animal/${animalId}`),
  getEvaluacion: (animalId) => api.get(`/controles_machos/evaluacion/${animalId}`),
};

// ADMIN - LICENCIAS
export const adminAPI = {
  getPlanes: () => api.get('/admin/planes'),
  getClientes: () => api.get('/admin/clientes'),
  getCliente: (clienteId) => api.get(`/admin/clientes/${clienteId}`),
  createCliente: (data) => api.post('/admin/clientes', data),
  updateCliente: (clienteId, data) => api.put(`/admin/clientes/${clienteId}`, data),
  suspenderCliente: (clienteId) => api.post(`/admin/clientes/${clienteId}/suspender`),
  reactivarCliente: (clienteId) => api.post(`/admin/clientes/${clienteId}/reactivar`),
};

// REPRODUCCION
export const reproduccionAPI = {
  getTemporada: (establecimientoId) => api.get(`/reproduccion/temporada/${establecimientoId}`),
  crearTemporada: (data) => api.post('/reproduccion/temporada', data),
  listarTemporadas: (establecimientoId) => api.get(`/reproduccion/temporadas/${establecimientoId}`),
  crearLote: (data) => api.post('/reproduccion/lotes', data),
  listarLotes: (establecimientoId, estado) => api.get(`/reproduccion/lotes/${establecimientoId}`, { params: { estado } }),
  getLote: (loteId) => api.get(`/reproduccion/lotes/detalle/${loteId}`),
  finalizarLote: (loteId) => api.put(`/reproduccion/lotes/${loteId}/finalizar`),
  registrarHabilidadMaterna: (paricionId, score) => api.post(`/reproduccion/habilidad-materna/${paricionId}?score=${score}`),
  getHabilidadMaterna: (animalId) => api.get(`/reproduccion/habilidad-materna/${animalId}`),
};

// REPORTES PDF
export const reportesAPI = {
  animalPDF: (animalId) => api.get(`/reportes/animal/${animalId}`, { responseType: 'blob' }),
  rodeoPDF: (establecimientoId) => api.get(`/reportes/rodeo/${establecimientoId}`, { responseType: 'blob' }),
};

// ANALYTICS
export const analyticsAPI = {
  tendencias: (establecimientoId, meses = 24) => api.get(`/analytics/tendencias/${establecimientoId}`, { params: { meses } }),
  comparacion: (userId) => api.get('/analytics/comparacion', { params: userId ? { user_id: userId } : {} }),
  comparativoTemporadas: (establecimientoId) => api.get(`/analytics/comparativo-temporadas/${establecimientoId}`),
};

// EMAIL REPORTS
export const emailReportsAPI = {
  enviar: (establecimientoId, email) => api.post(`/email-reports/enviar/${establecimientoId}`, { email }),
  preview: (establecimientoId) => api.get(`/email-reports/preview/${establecimientoId}`),
  getConfig: (establecimientoId) => api.get(`/email-reports/config/${establecimientoId}`),
  saveConfig: (establecimientoId, config) => api.post(`/email-reports/config/${establecimientoId}`, config),
  historial: (establecimientoId) => api.get(`/email-reports/historial/${establecimientoId}`),
};

// RENDIMIENTO VACAS
export const rendimientoVacasAPI = {
  getDashboard: (establecimientoId) => api.get(`/rendimiento-vacas/dashboard/${establecimientoId}`),
};

// INVERNADA / FEEDLOT
export const invernadaAPI = {
  crearLote: (data) => api.post('/invernada/lotes', data),
  getLotes: (establecimientoId) => api.get(`/invernada/lotes/${establecimientoId}`),
  finalizarLote: (loteId) => api.put(`/invernada/lotes/${loteId}/finalizar`),
  registrarAnimal: (data) => api.post('/invernada/animales', data),
  actualizarAnimal: (recordId, data) => api.put(`/invernada/animales/${recordId}`, data),
  getAnimalesLote: (loteId) => api.get(`/invernada/animales/${loteId}`),
  getDashboard: (establecimientoId, loteId) => api.get(`/invernada/dashboard/${establecimientoId}`, { params: loteId ? { lote_id: loteId } : {} }),
  getComparacion: (establecimientoId) => api.get(`/invernada/comparacion/${establecimientoId}`),
};

// DECISION SUPPORT
export const decisionAPI = {
  executive: (establecimientoId) => api.get(`/decision/executive/${establecimientoId}`),
  topCows: (establecimientoId) => api.get(`/decision/top-cows/${establecimientoId}`),
  reproductive: (establecimientoId) => api.get(`/decision/reproductive/${establecimientoId}`),
  bullFertility: (establecimientoId) => api.get(`/decision/bull-fertility/${establecimientoId}`),
  alerts: (establecimientoId) => api.get(`/decision/alerts/${establecimientoId}`),
};

// FRAME SCORE & EFICIENCIA
export const frameScoreAPI = {
  evaluacion: (establecimientoId, params = {}) => api.get(`/frame-score/evaluacion/${establecimientoId}`, { params }),
  eficienciaCria: (establecimientoId) => api.get(`/frame-score/eficiencia-cria/${establecimientoId}`),
  simulacionInvernada: (animalId, sistema = 'semi_intensivo') => api.get(`/frame-score/simulacion-invernada/${animalId}`, { params: { sistema } }),
  dashboardResumen: (establecimientoId) => api.get(`/frame-score/dashboard-resumen/${establecimientoId}`),
};

// ALERTAS PRODUCTIVAS
export const alertasAPI = {
  productivas: (establecimientoId) => api.get(`/alertas-productivas/${establecimientoId}`),
  invernada: (establecimientoId, sistema) => api.get(`/alertas-productivas/invernada/${establecimientoId}`, { params: sistema ? { sistema } : {} }),
  metricasAvanzadas: (establecimientoId) => api.get(`/alertas-productivas/metricas-avanzadas/${establecimientoId}`),
};

// CONFIG INVERNADA
export const configInvernadaAPI = {
  get: (establecimientoId) => api.get(`/config-invernada/${establecimientoId}`),
  update: (establecimientoId, data) => api.put(`/config-invernada/${establecimientoId}`, data),
};

// HITOS EVALUACION
export const hitosAPI = {
  animal: (animalId) => api.get(`/hitos/animal/${animalId}`),
  rodeo: (establecimientoId, sexo) => api.get(`/hitos/rodeo/${establecimientoId}`, { params: sexo ? { sexo } : {} }),
  produccionAcumulada: (establecimientoId) => api.get(`/hitos/produccion-acumulada/${establecimientoId}`),
};

// ESTABLECIMIENTOS
export const establecimientosAPI = {
  mis: (userId) => api.get(`/establecimientos/mis-establecimientos/${userId}`),
  crear: (userId, data) => api.post(`/establecimientos/crear/${userId}`, data),
  cambiar: (userId, estId) => api.put(`/establecimientos/cambiar/${userId}/${estId}`),
};

// DESCARTE
export const descarteAPI = {
  candidatos: (establecimientoId) => api.get(`/descarte/candidatos/${establecimientoId}`),
  marcar: (animalId, accion) => api.post(`/descarte/marcar/${animalId}?accion=${accion}`),
};

// EVENTOS DE CAMPO
export const eventosCampoAPI = {
  crearSesion: (data) => api.post('/eventos-campo/sesiones', data),
  listarSesiones: (estId, estado) => api.get(`/eventos-campo/sesiones/${estId}`, { params: estado ? { estado } : {} }),
  finalizarSesion: (sesionId) => api.put(`/eventos-campo/sesiones/${sesionId}/finalizar`),
  crearEvento: (data) => api.post('/eventos-campo/eventos', data),
  listarEventos: (sesionId) => api.get(`/eventos-campo/eventos/${sesionId}`),
  resumen: (estId) => api.get(`/eventos-campo/resumen/${estId}`),
};

// TEAM & USER INVITATIONS
export const teamAPI = {
  getTeam: (adminId) => api.get(`/auth/team/${adminId}`),
  inviteUser: (adminId, data) => api.post(`/auth/invite-user?admin_id=${adminId}`, data),
  removeMember: (adminId, userId) => api.delete(`/auth/team/${adminId}/member/${userId}`),
  cancelInvitation: (adminId, token) => api.delete(`/auth/team/${adminId}/invitation/${token}`),
  verifyUserInvite: (token) => api.get(`/auth/invite-user/${token}`),
  acceptUserInvite: (data) => api.post('/auth/accept-user-invite', data),
};

// SESIONES DE TRABAJO
export const sesionesTrabajoAPI = {
  crear: (data) => api.post('/sesiones-trabajo/', data),
  listar: (establecimientoId) => api.get('/sesiones-trabajo/', { params: { establecimiento_id: establecimientoId } }),
  obtener: (sesionId) => api.get(`/sesiones-trabajo/${sesionId}`),
  agregarRegistro: (sesionId, data) => api.post(`/sesiones-trabajo/${sesionId}/registro`, data),
  finalizar: (sesionId) => api.post(`/sesiones-trabajo/${sesionId}/finalizar`),
  eliminar: (sesionId) => api.delete(`/sesiones-trabajo/${sesionId}`),
  descargarExcel: (sesionId, nombre = 'sesion') => descargarArchivo(`/sesiones-trabajo/${sesionId}/descargar/excel`, `TKorveX_Sesion_${nombre}.xlsx`),
  descargarPdf: (sesionId, nombre = 'sesion') => descargarArchivo(`/sesiones-trabajo/${sesionId}/descargar/pdf`, `TKorveX_Sesion_${nombre}.pdf`),
};

// Descarga autenticada (blob) — evita "Token requerido" al abrir URLs del backend directamente
export const descargarArchivo = async (url, filename) => {
  const res = await api.get(url, { responseType: 'blob' });
  const blobUrl = window.URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.setAttribute('download', filename.replace(/[^\w.\-]+/g, '_'));
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
};

// COSTOS Y MARGEN
export const costosAPI = {
  registrar: (data) => api.post('/costos/', data),
  listar: (params) => api.get('/costos/', { params }),
  eliminar: (id) => api.delete(`/costos/${id}`),
  registrarVenta: (data) => api.post('/costos/ventas', data),
  listarVentas: (establecimientoId) => api.get('/costos/ventas', { params: { establecimiento_id: establecimientoId } }),
  eliminarVenta: (id) => api.delete(`/costos/ventas/${id}`),
  margen: (params) => api.get('/costos/margen', { params }),
};

// PROTOCOLOS REPRODUCTIVOS
export const protocolosAPI = {
  plantillas: () => api.get('/protocolos/plantillas'),
  crear: (data) => api.post('/protocolos/', data),
  listar: (params) => api.get('/protocolos/', { params }),
  recordatorios: (establecimientoId, dias = 7) => api.get('/protocolos/recordatorios', { params: { establecimiento_id: establecimientoId, dias } }),
  completarPaso: (protocoloId, pasoId) => api.put(`/protocolos/${protocoloId}/pasos/${pasoId}/completar`),
  eliminar: (id) => api.delete(`/protocolos/${id}`),
};

// INVENTARIO SANITARIO
export const inventarioAPI = {
  crearInsumo: (data) => api.post('/inventario/insumos', data),
  listarInsumos: (params) => api.get('/inventario/insumos', { params }),
  actualizarInsumo: (id, data) => api.put(`/inventario/insumos/${id}`, data),
  eliminarInsumo: (id) => api.delete(`/inventario/insumos/${id}`),
  movimiento: (id, data) => api.post(`/inventario/insumos/${id}/movimiento`, data),
  movimientos: (id) => api.get(`/inventario/insumos/${id}/movimientos`),
  alertas: (establecimientoId) => api.get('/inventario/alertas', { params: { establecimiento_id: establecimientoId } }),
};

export default api;
